import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { QueryFilters, type QueryFilterParams } from '@/lib/utils'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationIdInput,
  type ScopedAdminQueryKey,
  type ScopedAdminQueryRoot,
} from '@/lib/organization-scoping'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { collectionQueryKeys } from '../collections/collections.query'
import { inventoryQueryKeys } from '../inventory/inventory.keys'
import { organizationTaxSettingsQueryKeys } from '../organization-tax-settings/organization-tax-settings.query'
import { organizationQueryKeys } from '../organizations/organizations.query'
import { reportQueryKeys } from '../reports/reports.query'
import {
  createLocation,
  getLocation,
  getLocations,
  importLocationDeliveryPostalCodeFees,
  previewLocationDeliveryFee,
  previewLocationFulfillmentDate,
  previewLocationDeliveryPostalCodeFeeImport,
  softDeleteLocation,
  updateLocation,
  updateLocationDeliveryAvailabilitySettings,
  updateLocationDeliveryPricingSettings,
  updateLocationHoursSettings,
  updateLocationSettings,
} from './locations.api'
import type {
  CreateLocationInput,
  DeletedLocation,
  DeliveryFeePreviewInput,
  DeliveryPostalCodeFeeImportInput,
  Location,
  LocationDeliveryAvailabilitySettingsInput,
  LocationDeliveryPricingSettingsInput,
  LocationFulfillmentPreview,
  LocationFulfillmentPreviewInput,
  LocationHoursSettingsInput,
  LocationId,
  LocationSettingsInput,
  UpdateLocationInput,
} from './locations.types'

/** The key entries the location fetchers read; the scoped key builders below set them. */
export type LocationIndexQueryKey = [ScopedAdminQueryRoot & { filters?: QueryFilterParams }]

export type LocationShowQueryKey = [
  ScopedAdminQueryRoot & { locationId?: LocationId; filters?: QueryFilterParams },
]

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface LocationListScope extends OrganizationScope {
  filters?: QueryFilterParams
}

interface LocationDetailScope extends LocationListScope {
  locationId?: LocationId
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TData, TQueryKey extends readonly unknown[]> = Partial<
  Omit<UseQueryOptions<TData, Error, TData, TQueryKey>, 'queryKey' | 'queryFn'>
>

type SuspenseQueryParams<TData, TQueryKey extends readonly unknown[]> = Partial<
  Omit<UseSuspenseQueryOptions<TData, Error, TData, TQueryKey>, 'queryKey' | 'queryFn'>
>

interface LocationMutationValues {
  locationId: LocationId
}

export const locationFilters = () =>
  new QueryFilters({
    include: [
      'address',
      'deliveryPostalCodeFees',
      'deliveryCityFees',
      'deliveryDistanceFeeRanges',
      'deliveryAreaFees',
      'organization',
    ],
    sortBy: 'default',
  })

export const locationQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'location' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: LocationListScope = {}): LocationIndexQueryKey =>
    buildScopedAdminQueryKey(locationQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  show: ({ organizationId, locationId, filters }: LocationDetailScope = {}): LocationShowQueryKey =>
    buildScopedAdminQueryKey(locationQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      locationId,
      filters,
    }),
  fulfillmentPreview: ({
    organizationId,
    locationId,
    payload,
  }: OrganizationScope & {
    locationId?: LocationId
    payload?: LocationFulfillmentPreviewInput
  } = {}) =>
    buildScopedAdminQueryKey(locationQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'fulfillment-preview',
      locationId,
      payload,
    }),
}

export const locationQueries = {
  index: ({ organizationId, filters }: LocationListScope = {}) =>
    queryOptions({
      queryKey: locationQueryKeys.index({ organizationId, filters }),
      queryFn: getLocations,
      placeholderData: keepPreviousData,
    }),

  show: ({ organizationId, locationId, filters }: LocationDetailScope = {}) =>
    queryOptions({
      queryKey: locationQueryKeys.show({ organizationId, locationId, filters }),
      queryFn: getLocation,
      //Use data from the location index route if available
      initialData: () =>
        queryClient
          .getQueryData<Location[]>(locationQueryKeys.index({ organizationId, filters }))
          ?.find((l) => l.id === Number(locationId)),
      initialDataUpdatedAt: () =>
        queryClient.getQueryState(locationQueryKeys.index({ organizationId, filters }))
          ?.dataUpdatedAt,
    }),
}

export function useLocations({
  organizationId,
  filters,
  ...params
}: LocationListScope & SuspenseQueryParams<Location[], LocationIndexQueryKey> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...locationQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useLocation(
  locationId: LocationId,
  {
    organizationId,
    filters,
    ...params
  }: LocationListScope & SuspenseQueryParams<Location, LocationShowQueryKey> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...locationQueries.show({
      organizationId: resolvedOrganizationId,
      locationId,
      filters,
    }),
    ...params,
  })
}

export function useLocationFulfillmentPreview(
  locationId: LocationId,
  payload: LocationFulfillmentPreviewInput,
  {
    organizationId,
    enabled = true,
    ...params
  }: OrganizationScope & { enabled?: boolean } & Omit<
      QueryParams<LocationFulfillmentPreview, ScopedAdminQueryKey>,
      'enabled'
    > = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    queryKey: locationQueryKeys.fulfillmentPreview({
      organizationId: resolvedOrganizationId,
      locationId,
      payload,
    }),
    queryFn: () =>
      previewLocationFulfillmentDate({
        organizationId: resolvedOrganizationId,
        locationId,
        ...payload,
      }),
    enabled: Boolean(enabled && locationId),
    ...params,
  })
}

export function usePreviewLocationDeliveryFeeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DeliveryFeePreviewInput & LocationMutationValues) =>
      previewLocationDeliveryFee({ organizationId, ...values }),
  })
}

export function useUpdateLocationMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: UpdateLocationInput & LocationMutationValues) =>
      updateLocation({ organizationId, ...values }),
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({
          queryKey: reportQueryKeys.closedDays({ organizationId }),
        }),
        // The org's default timezone could have changed...
        queryClient.invalidateQueries({ queryKey: organizationQueryKeys.show(organizationId) }),
        queryClient.invalidateQueries({
          queryKey: organizationTaxSettingsQueryKeys.all({ organizationId }),
        }),
      ])
    },
  })
}

export function useUpdateLocationSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: LocationSettingsInput & LocationMutationValues) =>
      updateLocationSettings({ organizationId, ...values }),
    onSuccess: () => invalidateLocationSettingsQueries(organizationId, { closedDaysReport: true }),
  })
}

function invalidateLocationSettingsQueries(
  organizationId: number,
  { closedDaysReport = false }: { closedDaysReport?: boolean } = {}
) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
  ]

  if (closedDaysReport) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: reportQueryKeys.closedDays({ organizationId }),
      })
    )
  }

  return Promise.all(invalidations)
}

export function useUpdateLocationHoursSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (
      values: LocationHoursSettingsInput & LocationMutationValues & { previousIsPickup?: boolean }
    ) => {
      const payload = { ...values }
      delete payload.previousIsPickup

      return updateLocationHoursSettings({ organizationId, ...payload })
    },
    onSuccess: (_data, { is_pickup, previousIsPickup }) =>
      invalidateLocationSettingsQueries(organizationId, {
        closedDaysReport: previousIsPickup === undefined || is_pickup !== previousIsPickup,
      }),
  })
}

export function useUpdateLocationDeliveryAvailabilitySettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (
      values: LocationDeliveryAvailabilitySettingsInput &
        LocationMutationValues & { previousIsDelivery?: boolean }
    ) => {
      const payload = { ...values }
      delete payload.previousIsDelivery

      return updateLocationDeliveryAvailabilitySettings({ organizationId, ...payload })
    },
    onSuccess: (_data, { is_delivery, previousIsDelivery }) =>
      invalidateLocationSettingsQueries(organizationId, {
        closedDaysReport: previousIsDelivery === undefined || is_delivery !== previousIsDelivery,
      }),
  })
}

export function useUpdateLocationDeliveryPricingSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: LocationDeliveryPricingSettingsInput & LocationMutationValues) =>
      updateLocationDeliveryPricingSettings({ organizationId, ...values }),
    onSuccess: () => invalidateLocationSettingsQueries(organizationId),
  })
}

export function usePreviewLocationDeliveryPostalCodeFeeImportMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DeliveryPostalCodeFeeImportInput & LocationMutationValues) =>
      previewLocationDeliveryPostalCodeFeeImport({ organizationId, ...values }),
  })
}

export function useImportLocationDeliveryPostalCodeFeesMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DeliveryPostalCodeFeeImportInput & LocationMutationValues) =>
      importLocationDeliveryPostalCodeFees({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
      ]),
  })
}

export function useCreateLocationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateLocationInput) => createLocation({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: organizationTaxSettingsQueryKeys.all({ organizationId }),
        }),
        queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({
          queryKey: reportQueryKeys.closedDays({ organizationId }),
        }),
      ]),
  })
}

const cleanupDeletedLocationQueries = ({
  organizationId,
  locationId,
}: {
  organizationId: number
  locationId: LocationId
}) => {
  queryClient.removeQueries({
    queryKey: locationQueryKeys.show({ organizationId, locationId }),
  })

  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: organizationTaxSettingsQueryKeys.all({ organizationId }),
    }),
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({
      queryKey: reportQueryKeys.closedDays({ organizationId }),
    }),
  ])
}

export function useSoftDeleteLocationMutation({
  cleanupOnSuccess = true,
  onSuccess,
}: {
  cleanupOnSuccess?: boolean
  onSuccess?: (response: DeletedLocation) => void
} = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedLocation = (locationId: LocationId) =>
    cleanupDeletedLocationQueries({ organizationId, locationId })

  const mutation = useMutation({
    mutationFn: (locationId: LocationId) => softDeleteLocation({ organizationId, locationId }),
    onSuccess: (response, locationId) => {
      onSuccess?.(response)
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedLocation(locationId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedLocation,
  }
}
